import { useCallback, useEffect, useState } from 'react';
import { Grid, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, ListItemText, ListItemIcon, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useHealthPlanContext } from '../../../hooks/healthPlanContext';
import { MenuProps } from './AnalyticService';

const monthsList = [
    "January", "February", "March", "April",
    "May", "June", "July", "August",
    "September", "October", "November", "December"
];

const FilterOptions = (props) => {
    const {
        healthPlanIds,
        setHealthPlanIds,
        team,
        setTeam,
        showInActiveTeams,
        setShowInActiveTeams,
        years,
        setYears,
        months,
        setMonths,
        onFetch,
    } = props;
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { population } = useSelector((state: RootState) => state.adminMembers);
    const { healthPlans } = useHealthPlanContext();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const filteredTeams = (showInActiveTeams ? teams : teams.filter((t) => !t.IsDeleted)) || [];
    const filteredHealthPlans = healthPlans
        .filter((item) => {
            return population.map((item) => item.HealthPlan?.Name).includes(item.Name);
        })
        .sort((a, b) => a.Name.localeCompare(b.Name));

    const currentYear = new Date().getFullYear();
    const yearOptions = [...Array.from({ length: 4 }, (_, i) => currentYear - i), 0];
    const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

    const getYearLabel = (year: number) => {
        if (year === 0) return "Baseline";
        return year.toString();
    };

    const safeMonths = months?.map(Number) || [];

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        setYears(years);
        setMonths(months);
        setIsFetched(true);
    }, [isFetched, setIsFetched]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (months.length === 0) {
            setMonths(monthOptions);
        }
    }, [monthOptions]);

    const toggleTeamHandler = (isChecked: boolean) => {
        setShowInActiveTeams(isChecked);
        setTeam('All');
    };

    useEffect(() => {
        if (filteredHealthPlans.length >= 2 && setHealthPlanIds && healthPlanIds.length === 0) {
            setHealthPlanIds([
                filteredHealthPlans[0].Id,
                filteredHealthPlans[1].Id
            ]);
        }
    }, [filteredHealthPlans]);

    return (
        <>
            <Grid size={2.6}>
                <FormControl size="small" fullWidth>
                    <InputLabel>Team</InputLabel>
                    <Select
                        value={team}
                        label="Team"
                        name="Team"
                        onChange={(e) => {
                            setTeam(e.target.value);
                        }}
                        MenuProps={MenuProps}
                    >
                        <MenuItem key="All" value="All">
                            All
                        </MenuItem>
                        <FormControlLabel
                            sx={{ pl: '12px', mb: '0px' }}
                            control={<Checkbox checked={showInActiveTeams} onChange={(e) => toggleTeamHandler(e.target.checked)} />}
                            label="Show Inactive"
                        />
                        {filteredTeams.map((team) => (
                            <MenuItem key={team.Id} value={team.Id} sx={{ color: team.IsDeleted ? 'grey' : 'black' }}>
                                {team.Name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={2.6}>
                <FormControl size="small" fullWidth>
                    <InputLabel>Health Plan</InputLabel>
                    <Select
                        multiple
                        value={Array.isArray(healthPlanIds) ? healthPlanIds : []}
                        label="Health Plan"
                        name="plan"
                        onChange={(e) => {
                            let value = e.target.value;
                            value = typeof value === 'string' ? value.split(',') : value;

                            if (value.includes("All")) {
                                const allIds = filteredHealthPlans.map(p => p.Id);
                                setHealthPlanIds(allIds);
                            } else {
                                setHealthPlanIds(value);
                            }
                        }}
                        renderValue={(selected) => {
                            if (selected.length === filteredHealthPlans.length) {
                                return "All";
                            }

                            return filteredHealthPlans
                                .filter(plan => selected.includes(plan.Id))
                                .map(plan => plan.Name)
                                .join(", ");
                        }}
                        MenuProps={MenuProps}
                    >
                        <MenuItem value="All">
                            <ListItemIcon>
                                {healthPlanIds.length === filteredHealthPlans.length && (
                                    <span style={{ fontSize: "14px" }}>✔</span>
                                )}
                            </ListItemIcon>
                            <ListItemText primary="All" />
                        </MenuItem>

                        {filteredHealthPlans.map((plan) => (
                            <MenuItem key={plan.Id} value={plan.Id}>
                                <ListItemIcon>
                                    {healthPlanIds.includes(plan.Id) && (
                                        <span style={{ fontSize: "14px" }}>✔</span>
                                    )}
                                </ListItemIcon>
                                <ListItemText primary={plan.Name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={2.6}>
                <FormControl size="small" fullWidth>
                    <InputLabel>Year</InputLabel>
                    <Select
                        multiple
                        value={Array.isArray(years) ? years : []}
                        label="Year"
                        onChange={(e) => {
                            let value = e.target.value;
                            value = typeof value === "string" ? value.split(",") : value;

                            if (value.includes("All")) {
                                setYears(yearOptions.map(String));
                            } else if (value.length === 0) {
                                setYears([String(currentYear)]);
                            } else {
                                setYears(value);
                            }
                        }}
                        renderValue={(selected) => {
                            if (!selected || selected.length === 0) return "";

                            if (selected.length === yearOptions.length) return "All";

                            return selected.map((y) => getYearLabel(Number(y))).join(", ");
                        }}
                    >
                        <MenuItem value="All">
                            <ListItemIcon>
                                {years.length === yearOptions.length && (
                                    <span style={{ fontSize: "14px" }}>✔</span>
                                )}
                            </ListItemIcon>
                            <ListItemText primary="All" />
                        </MenuItem>

                        {yearOptions.map((y) => (
                            <MenuItem key={y} value={String(y)}>
                                <ListItemIcon>
                                    {years.includes(String(y)) && (
                                        <span style={{ fontSize: "14px" }}>✔</span>
                                    )}
                                </ListItemIcon>
                                <ListItemText primary={getYearLabel(y)} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={2.6}>
                <FormControl size="small" fullWidth>
                    <InputLabel>Month</InputLabel>
                    <Select
                        multiple
                        value={safeMonths}
                        label="Month"
                        onChange={(e) => {
                            let value = e.target.value;

                            value = typeof value === "string"
                                ? value.split(",").map(Number)
                                : value;

                            if (value.includes("All")) {
                                setMonths(monthOptions);
                            } else if (value.length === 0) {
                                setMonths(monthOptions);
                            } else {
                                setMonths(value);
                            }
                        }}
                        renderValue={(selected) => {
                            if (!selected || selected.length === 0) return "All";

                            if (selected.length === monthOptions.length) return "All";

                            return selected.map(m => monthsList[m - 1]).join(", ");
                        }}
                    >
                        <MenuItem value="All">
                            <ListItemIcon>
                                {months.length === monthOptions.length && (
                                    <span style={{ fontSize: "14px" }}>✔</span>
                                )}
                            </ListItemIcon>
                            <ListItemText primary="All" />
                        </MenuItem>

                        {monthsList.map((month, index) => {
                            const value = index + 1;

                            return (
                                <MenuItem key={value} value={value}>
                                    <ListItemIcon>
                                        {safeMonths.includes(value) && (
                                            <span style={{ fontSize: "14px" }}>✔</span>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText primary={month} />
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={1.5}>
                <Button
                    style={{ minWidth: '80px' }}
                    variant="contained"
                    onClick={onFetch}>
                    Submit
                </Button>
            </Grid>
        </>
    );
};

export default FilterOptions;
